import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Typography, Card, Row, Col, Modal, Descriptions, Form, Select, InputNumber, Space, DatePicker, Input as AntInput, Grid, Popconfirm } from 'antd';
import { PlusOutlined, ReloadOutlined, EyeOutlined, PlayCircleOutlined, CheckCircleOutlined, SendOutlined, DeleteOutlined } from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MillingPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { hasPermission, user } = useAuth();
    const isManagerOrAdmin = ['ADMIN', 'MANAGER'].includes(user?.role);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const navigate = useNavigate();

    const [viewOpen, setViewOpen] = useState(false);
    const [viewOrder, setViewOrder] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [yieldOpen, setYieldOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [rowLoading, setRowLoading] = useState({}); // Tracking loading per row ID
    const [yieldForm] = Form.useForm();
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchOrders();
        fetchItems();
    }, [dates]);

    useEffect(() => {
        if (yieldOpen && viewOrder && items.length > 0) {
            suggestOutputs();
        }
    }, [yieldOpen, viewOrder, items]);

    const suggestOutputs = () => {
        if (!viewOrder || !viewOrder.inputs) return;

        const inputPalays = viewOrder.inputs
            .map(i => i.lot?.item?.name)
            .filter(name => !!name);

        const suggestedOutputs = [];

        inputPalays.forEach(palayName => {
            const matchingRice = items.find(i =>
                i.name &&
                i.category?.slug === 'rice' &&
                (i.name.toLowerCase().includes(palayName.toLowerCase()) || palayName.toLowerCase().includes(i.name.toLowerCase()))
            );

            if (matchingRice) {
                if (!suggestedOutputs.some(s => s.item_id === matchingRice.id)) {
                    suggestedOutputs.push({
                        item_id: matchingRice.id,
                        output_type: 'FINISHED_GOOD',
                        quantity: 0
                    });
                }
            }
        });

        const byproduct = items.find(i => i.name && i.category?.slug === 'by-product' && i.name.toLowerCase().includes('darak'));
        if (byproduct) {
            suggestedOutputs.push({
                item_id: byproduct.id,
                output_type: 'BYPRODUCT',
                quantity: 0
            });
        }

        if (suggestedOutputs.length > 0) {
            yieldForm.setFieldsValue({ outputs: suggestedOutputs });
        } else {
            yieldForm.setFieldsValue({ outputs: [{ output_type: 'FINISHED_GOOD' }] });
        }
    };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const params = {};
            if (dates) {
                params.start_date = dates[0].format('YYYY-MM-DD');
                params.end_date = dates[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get('/transactions/milling', { params });
            setOrders(data);
        } catch (error) {
            message.error('Failed to fetch milling orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchItems = async () => {
        try {
            const { data } = await client.get('/master/items');
            setItems(data);
        } catch (error) {
            message.error('Failed to load items');
        }
    };

    const handleView = async (id) => {
        setViewLoading(true);
        setViewOpen(true);
        try {
            const { data } = await client.get(`/transactions/milling/${id}`);
            setViewOrder(data);
        } catch (error) {
            message.error('Failed to load order details');
        } finally {
            setViewLoading(false);
        }
    };

    const handleStart = async (id) => {
        setRowLoading(prev => ({ ...prev, [id]: true }));
        try {
            await client.post(`/transactions/milling/${id}/start`);
            message.success('Milling process started');
            fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Start failed');
        } finally {
            setRowLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleRecordYield = async (values) => {
        setActionLoading(true);
        try {
            await client.post(`/transactions/milling/${viewOrder.id}/yield`, values);
            message.success('Production yield recorded');
            setYieldOpen(false);
            yieldForm.resetFields();
            fetchOrders();
            handleView(viewOrder.id);
        } catch (error) {
            message.error(error.response?.data?.message || 'Recording yield failed');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePost = async (id) => {
        setRowLoading(prev => ({ ...prev, [id]: true }));
        try {
            await client.post(`/transactions/milling/${id}/post`);
            message.success('Production posted to inventory and accounting');
            fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Posting failed');
        } finally {
            setRowLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleCancel = async (id) => {
        setRowLoading(prev => ({ ...prev, [id]: true }));
        try {
            await client.post(`/transactions/milling/${id}/cancel`);
            message.success('Milling order cancelled and lots released');
            fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Cancel failed');
        } finally {
            setRowLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/transactions/milling/${id}`);
            message.success('Milling order deleted');
            fetchOrders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Delete failed');
        }
    };


    const columns = [
        {
            title: 'Batch Number',
            dataIndex: 'order_number',
            key: 'order_number',
            render: (text) => <Typography.Text strong style={{ color: '#3b82f6' }}>{text}</Typography.Text>,
        },
        {
            title: 'Processing Branch',
            dataIndex: ['branch', 'name'],
            key: 'branch',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Operation Date',
            dataIndex: 'milling_date',
            key: 'milling_date',
            render: (date) => <Typography.Text style={{ color: 'var(--text-secondary)' }}>{dayjs(date).format('MMM D, YYYY')}</Typography.Text>,
        },
        {
            title: 'Mill Operator',
            dataIndex: 'mill_operator',
            key: 'mill_operator',
        },
        {
            title: 'Input Quantity',
            dataIndex: 'total_input_quantity',
            key: 'total_input_quantity',
            render: (qty) => <Typography.Text strong>{Number(qty).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} kg</Typography.Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status === 'COMPLETED' || status === 'POSTED') color = 'success';
                if (status === 'IN_PROGRESS') color = 'processing';
                if (status === 'CANCELLED') color = 'error';
                return <Tag color={color} style={{ borderRadius: 12, padding: '0 12px', fontWeight: 600 }}>{status}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record.id)}
                    >
                        View
                    </Button>
                    {record.status === 'DRAFT' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleStart(record.id)}
                            loading={rowLoading[record.id]}
                        >
                            Start
                        </Button>
                    )}
                    {record.status === 'IN_PROGRESS' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => {
                                setViewOrder(record);
                                setYieldOpen(true);
                            }}
                            style={{ background: '#10b981', borderColor: '#10b981' }}
                        >
                            Yield
                        </Button>
                    )}
                    {record.status === 'COMPLETED' && hasPermission('post_transaction') && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => handlePost(record.id)}
                            style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}
                            loading={rowLoading[record.id]}
                        >
                            Post
                        </Button>
                    )}
                    {(record.status === 'DRAFT' || record.status === 'IN_PROGRESS') && isManagerOrAdmin && (
                        <Popconfirm
                            title="Cancel this milling order?"
                            description={record.status === 'IN_PROGRESS' ? 'Consumed lot quantities will be released back to stock.' : undefined}
                            onConfirm={() => handleCancel(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button size="small" danger loading={rowLoading[record.id]}>Cancel</Button>
                        </Popconfirm>
                    )}
                    {record.status === 'DRAFT' && isManagerOrAdmin && (
                        <Popconfirm title="Delete this milling order?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const filteredOrders = orders.filter((o) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            (o.order_number || '').toLowerCase().includes(term) ||
            (o.branch?.name || '').toLowerCase().includes(term) ||
            (o.status || '').toLowerCase().includes(term)
        );
    });

    const stats = {
        total: filteredOrders.length,
        completed: filteredOrders.filter(o => o.status === 'COMPLETED').length,
        inProgress: filteredOrders.filter(o => o.status === 'IN_PROGRESS').length,
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Milling Operations</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Track grain processing, milling batches, and production yields.</Typography.Text>
                </div>
                <Space wrap>
                    <RangePicker
                        value={dates}
                        onChange={(val) => setDates(val)}
                        style={{ borderRadius: 8, width: isMobile ? '100%' : undefined }}
                    />
                    <ExportButton
                        filename="milling-operations"
                        sheetName="Milling"
                        title="Milling Operations"
                        headers={['Batch #', 'Branch', 'Date', 'Operator', 'Input Qty (kg)', 'Recovery Rate', 'Status']}
                        getData={() => filteredOrders.map(o => [
                            o.order_number,
                            o.branch?.name || '',
                            dayjs(o.milling_date).format('YYYY-MM-DD'),
                            o.mill_operator,
                            o.total_input_quantity,
                            o.recovery_rate ? `${Number(o.recovery_rate).toFixed(2)}%` : '',
                            o.status,
                        ])}
                        style={{ borderRadius: 8 }}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchOrders}
                        style={{ borderRadius: 8 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => navigate('/milling/new')}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                    >
                        New Milling Order
                    </Button>
                </Space>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Batches"
                        value={stats.total}
                        icon={<i className="fa-solid fa-industry"></i>}
                        color="#3b82f6"
                        subText="All processing jobs"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Completed"
                        value={stats.completed}
                        icon={<i className="fa-solid fa-circle-check"></i>}
                        color="#10b981"
                        subText="Finished production"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="In Progress"
                        value={stats.inProgress}
                        icon={<i className="fa-solid fa-spinner fa-spin-pulse"></i>}
                        color="#f59e0b"
                        subText="Active processing"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
                    <AntInput
                        placeholder="Search batch #, branch, status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: isMobile ? '100%' : 340, borderRadius: 10, height: 40 }}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={filteredOrders}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1100 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title="Milling Order Details"
                open={viewOpen}
                onCancel={() => setViewOpen(false)}
                footer={null}
                width={isMobile ? '100%' : 800}
                style={{ top: isMobile ? 12 : 24 }}
                bodyStyle={{ padding: isMobile ? 12 : 24 }}
            >
                {viewLoading ? (
                    <Typography.Text>Loading...</Typography.Text>
                ) : viewOrder ? (
                    <>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
                            <Descriptions.Item label="Batch #">{viewOrder.order_number}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={viewOrder.status === 'POSTED' ? 'success' : 'processing'}>{viewOrder.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Date">{dayjs(viewOrder.milling_date).format('MMM D, YYYY')}</Descriptions.Item>
                            <Descriptions.Item label="Operator">{viewOrder.mill_operator}</Descriptions.Item>
                            <Descriptions.Item label="Input Qty">{Number(viewOrder.total_input_quantity).toLocaleString()} kg</Descriptions.Item>
                            <Descriptions.Item label="Recovery Rate">
                                <Typography.Text strong style={{ color: viewOrder.recovery_rate < 60 ? '#ef4444' : '#10b981' }}>
                                    {viewOrder.recovery_rate ? `${Number(viewOrder.recovery_rate).toFixed(2)}%` : 'N/A'}
                                </Typography.Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Typography.Title level={5}>Input Materials (Consumption)</Typography.Title>
                        <Table
                            dataSource={viewOrder.inputs || []}
                            size="small"
                            pagination={false}
                            columns={[
                                {
                                    title: 'Lot Number',
                                    key: 'lot',
                                    render: (_, record) => (
                                        <span>
                                            {record.lot?.lot_number || 'N/A'}
                                            {record.lot?.item?.name ? ` - ${record.lot.item.name}` : ''}
                                        </span>
                                    )
                                },
                                { title: 'Quantity', dataIndex: 'quantity', key: 'qty', render: (v) => `${Number(v).toLocaleString()} kg` },
                            ]}
                            style={{ marginBottom: 24 }}
                        />

                        <Typography.Title level={5}>Production Outputs (Yield)</Typography.Title>
                        <Table
                            dataSource={viewOrder.outputs || []}
                            size="small"
                            pagination={false}
                            columns={[
                                { title: 'Item', dataIndex: ['item', 'name'], key: 'item' },
                                { title: 'Type', dataIndex: 'output_type', key: 'type' },
                                { title: 'Actual Qty', dataIndex: 'quantity', key: 'qty', render: (v) => `${Number(v).toLocaleString()} kg` },
                                { title: 'Expected', dataIndex: 'expected_quantity', key: 'expected', render: (v) => v ? `${Number(v).toLocaleString()} kg` : '-' },
                            ]}
                        />
                    </>
                ) : null}
            </Modal>

            <Modal
                title="Record Milling Yield"
                open={yieldOpen}
                onCancel={() => {
                    setYieldOpen(false);
                    yieldForm.resetFields();
                }}
                footer={null}
                width={700}
            >
                <Form form={yieldForm} layout="vertical" onFinish={handleRecordYield}>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        Record the total production yield from batch <strong>{viewOrder?.order_number}</strong>.
                    </Typography.Text>

                    <Form.List name="outputs">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <div key={key} style={{ background: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                                        <Row gutter={16}>
                                            <Col span={10}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'item_id']}
                                                    label="Output Item"
                                                    rules={[{ required: true }]}
                                                >
                                                    <Select placeholder="Select product" showSearch optionFilterProp="children">
                                                        {items
                                                            .filter(i => i.category?.slug === 'rice' || i.category?.slug === 'by-product')
                                                            .map(i => <Option key={i.id} value={i.id}>{i.name}</Option>)
                                                        }
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'output_type']}
                                                    label="Type"
                                                    rules={[{ required: true }]}
                                                >
                                                    <Select>
                                                        <Option value="FINISHED_GOOD">Finished Good</Option>
                                                        <Option value="BYPRODUCT">Byproduct</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'quantity']}
                                                    label="Qty (kg)"
                                                    rules={[{ required: true }]}
                                                >
                                                    <InputNumber style={{ width: '100%' }} placeholder="0.00" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
                                                {fields.length > 1 && (
                                                    <Button type="link" danger onClick={() => remove(name)} icon={<PlusOutlined rotate={45} />} />
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginBottom: 24 }}>
                                    Add Output Item
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <Button type="primary" htmlType="submit" block size="large" loading={actionLoading}>
                        Complete Batch & Record Yield
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};

export default MillingPage;




